import { getText } from "shared/lib";
import {
  TreePropertyType,
  updateTreeProperty,
  type ExcavationTreePropertyDescriptor,
  type TreeDescriptor,
} from "../lib";
import { useTreeDialogValidator } from "./lib";

import { Toggle } from "shared/components";
import { FieldLabel } from "./FieldLabel";
import { FieldValidationMessages } from "./FieldValidationMessages";

export interface ExcavationFieldProps {
  onChange: (treeDescriptor: TreeDescriptor) => void;
  treeDescriptor: TreeDescriptor;
}

export function ExcavationField<T extends ExcavationTreePropertyDescriptor>({
  onChange,
  treeDescriptor,
}: ExcavationFieldProps) {
  const { isValid, messages, shouldShowValidation } = useTreeDialogValidator<T>(
    {
      checker: (property) => !!property?.value,
      properties: treeDescriptor.properties,
      treePropertyType: TreePropertyType.excavation,
    },
  );

  const excavation = treeDescriptor.properties.get(
    TreePropertyType.excavation,
  ) as T | undefined;

  const handleChange = (value: string) => {
    const newTreeDescriptor = updateTreeProperty<T>(
      treeDescriptor,
      TreePropertyType.excavation,
      (draft) => {
        draft.value = value as T["value"];
      },
    );

    onChange?.(newTreeDescriptor);
  };

  return (
    <div className="flex flex-col gap-1">
      <FieldLabel isValid={isValid}>
        {getText("Will your proposed works alter the ground level?")}
      </FieldLabel>

      <div className="flex gap-2">
        <Toggle
          aria-invalid={!isValid && shouldShowValidation}
          className="aria-invalid:border-redBorder"
          onClick={() => {
            handleChange("yes");
          }}
          pressed={excavation?.value === "yes"}
          variant="rounded"
        >
          {getText("Yes")}
        </Toggle>
        <Toggle
          aria-invalid={!isValid && shouldShowValidation}
          className="aria-invalid:border-redBorder"
          onClick={() => {
            handleChange("no");
          }}
          pressed={excavation?.value === "no"}
          variant="rounded"
        >
          {getText("No")}
        </Toggle>
        <Toggle
          aria-invalid={!isValid && shouldShowValidation}
          className="aria-invalid:border-redBorder"
          onClick={() => {
            handleChange("unsure");
          }}
          pressed={excavation?.value === "unsure"}
          variant="rounded"
        >
          {getText("Unsure")}
        </Toggle>
      </div>

      <FieldValidationMessages messages={messages} />
    </div>
  );
}
